import { useSearchParams } from 'react-router';
import { useFlightsMonitor } from '@/lib/flights/use-flights';
import { airportsCsv, useAirportScope } from '@/lib/operator-context/airport-scope';
import type { FlightDirection, FlightMonitorFilters } from '@/lib/flights/types';

// Reads the monitor filters from the URL (NEX-342 §2: filter state survives
// reload — links are shareable). Mirrors routes/cases/use-cases-list.ts. Without a
// date window the board is TODAY's (2026-10-26): what the operator still needs to see —
// the BFF's `today-board.ts` says which flights that is.
// `?airports=` is a CSV of IATA codes (the self-contained scope filter — same
// contract so it later swaps to the shared topbar picker, NEX-310).

const DIRECTIONS: readonly FlightDirection[] = ['any', 'origin', 'destination'];

function readDirection(raw: string | null): FlightDirection | undefined {
  if (raw && (DIRECTIONS as readonly string[]).includes(raw)) return raw as FlightDirection;
  return undefined;
}

export function useFlightsList() {
  const [params] = useSearchParams();
  // The header airport scope, like Cases and Disruptions (PO 2026-10-28: a zoned operator
  // «debería ver solo llegadas y salidas de Ezeiza»): the selection bounded by the profile's
  // airports, «Mis aeropuertos» by default. The BFF enforces the zone again on its own.
  const scope = useAirportScope();
  const from = params.get('from') || undefined;
  const to = params.get('to') || undefined;
  const filters: FlightMonitorFilters = {
    airports: airportsCsv(scope.airports),
    direction: readDirection(params.get('direction')),
    status: params.get('status') || undefined,
    q: params.get('q') || undefined,
    from,
    to,
    disrupted: params.get('disrupted') === '1' || undefined,
    // No date window asked for → today's board (`?board=today`); dates → a lookup.
    board: from || to ? undefined : 'today',
  };
  return { ...useFlightsMonitor(filters), today: filters.board === 'today' };
}
